import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import { useNavigate, Link } from 'react-router-dom';

const NAV_ITEMS = {
    introduction: 'Introduction',
    infoCollect: 'Information We Collect',
    howWeUse: 'How We Use Your Information',
    cookies: 'Cookies & Tracking Technologies',
    advertising: 'Advertising & Google AdSense',
    dataSharing: 'How We Share Information',
    dataRetention: 'Data Retention',
    yourRights: 'Your Rights & Choices',
    childrenPrivacy: "Children's Privacy",
    international: 'International Data Transfers',
    security: 'Data Security',
    thirdPartyLinks: 'Third-Party Links',
    changes: 'Changes To This Policy',
    contact: 'Contact Us',
};

const PrivacyPolicy = () => {
    const sectionRefs = {
        introduction: useRef(null),
        infoCollect: useRef(null),
        howWeUse: useRef(null),
        cookies: useRef(null),
        advertising: useRef(null),
        dataSharing: useRef(null),
        dataRetention: useRef(null),
        yourRights: useRef(null),
        childrenPrivacy: useRef(null),
        international: useRef(null),
        security: useRef(null),
        thirdPartyLinks: useRef(null),
        changes: useRef(null),
        contact: useRef(null),
    };

    const [activeTab, setActiveTab] = useState('introduction');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            const mainContent = document.querySelector('.privacy-main-content');
            if (mainContent) {
                const scrollTop = mainContent.scrollTop;
                const scrollHeight = mainContent.scrollHeight - mainContent.clientHeight;
                const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
                setScrollProgress(progress);
            }
        };

        const mainContent = document.querySelector('.privacy-main-content');
        if (mainContent) {
            mainContent.addEventListener('scroll', handleScroll);
            return () => mainContent.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const scrollToSection = (ref, sectionId) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
        setActiveTab(sectionId);
        setIsMobileMenuOpen(false);
    };

    const sidebarItemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: { delay: i * 0.05, duration: 0.3 },
        }),
    };

    const contentVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: 'easeOut' },
        },
    };

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const NavList = ({ mobile }) => (
        <nav className={mobile ? 'space-y-1 px-4' : 'space-y-1'}>
            {Object.entries(NAV_ITEMS).map(([id, title], index) => (
                <motion.button
                    key={id}
                    custom={index}
                    initial="hidden"
                    animate="visible"
                    variants={sidebarItemVariants}
                    onClick={() => scrollToSection(sectionRefs[id], id)}
                    className={`w-full text-left px-4 ${mobile ? 'py-3' : 'py-2'} rounded-md flex items-center ${activeTab === id
                        ? 'bg-customGold text-white font-medium'
                        : 'text-white hover:bg-gray-600'
                        }`}
                >
                    <HiOutlineChevronRight className="mr-2 shrink-0" />
                    {title}
                </motion.button>
            ))}
        </nav>
    );

    return (
        <div className="flex flex-col md:flex-row h-screen bg-customDark">
            {/* Mobile Menu Button */}
            <div className="md:hidden fixed bottom-4 right-4 z-50">
                <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-md bg-customGold text-white"
                    aria-label="Toggle navigation menu"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-70 z-40 md:hidden"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Sidebar Navigation - Mobile */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'tween', ease: 'easeInOut' }}
                        className="fixed inset-y-0 left-0 w-64 bg-customGray z-40 overflow-y-auto md:hidden"
                    >
                        <div className="p-4 flex items-center space-x-3">
                            <button onClick={() => navigate(-1)} className="text-white hover:text-customGold" aria-label="Go back">
                                <HiOutlineChevronLeft size={24} />
                            </button>
                            <h2 className="text-xl font-bold text-white">Privacy Policy</h2>
                        </div>
                        <NavList mobile />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Sidebar Navigation - Desktop */}
            <div className="hidden md:block w-64 bg-customGray overflow-y-auto sticky top-0 h-screen">
                <div className="p-4">
                    <div className="flex items-center gap-3 mb-6">
                        <button onClick={() => navigate(-1)} className="text-white hover:text-customGold transition" aria-label="Go back">
                            <HiOutlineChevronLeft size={24} />
                        </button>
                        <motion.h2
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-xl font-bold text-white"
                        >
                            Privacy Policy
                        </motion.h2>
                    </div>
                    <NavList mobile={false} />
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-8 privacy-main-content main-content relative">
                <motion.div
                    className="fixed top-0 left-0 right-0 h-1 bg-customGold z-30"
                    style={{ width: `${scrollProgress}%` }}
                    initial={{ width: 0 }}
                />

                <motion.div initial="hidden" animate="visible" variants={contentVariants} className="mb-8">
                    <p className="text-lightA text-sm">Last updated: August 20, 2026</p>
                </motion.div>

                {/* Introduction */}
                <motion.section ref={sectionRefs.introduction} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h2 className="text-2xl font-bold border-b border-gray-200 pb-2">INTRODUCTION</h2>
                    <p>
                        This Privacy Policy explains how Blackdefynition ("we," "us," or "our") collects, uses, discloses, and safeguards your information when you visit or use blackdefynition.com and the BD social, marketplace, Naray8, and wallet features (collectively, the "Site" or "Service").
                    </p>
                    <p>
                        By using the Site, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with this policy, please do not use the Site. This Privacy Policy should be read together with our{' '}
                        <Link to="/terms" className="text-customGold hover:underline">Terms &amp; Conditions</Link>.
                    </p>
                </motion.section>

                {/* Information We Collect */}
                <motion.section ref={sectionRefs.infoCollect} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Information We Collect</h3>
                    <p>We collect the following categories of information:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                        <li><strong>Account information:</strong> name, username, email address, phone number, date of birth, gender, country, and password.</li>
                        <li><strong>Content you provide:</strong> posts, comments, photos, videos, stories, reels, chapters and stories uploaded to Naray8, messages, group and event details, and other Submissions.</li>
                        <li><strong>Marketplace and wallet data:</strong> order history, delivery address, transaction records, and BD Coins/wallet balance and activity.</li>
                        <li><strong>Usage data:</strong> pages visited, features used, links clicked, and time spent on the Site.</li>
                        <li><strong>Device and log data:</strong> IP address, browser type, operating system, device identifiers, and referring URLs.</li>
                        <li><strong>Cookies and similar technologies:</strong> as described in the Cookies &amp; Tracking Technologies section below.</li>
                    </ul>
                </motion.section>

                {/* How We Use Your Information */}
                <motion.section ref={sectionRefs.howWeUse} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">How We Use Your Information</h3>
                    <p>We use the information we collect to:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                        <li>Create and maintain your account and provide the Site's features (social feed, groups, events, Naray8, wallet, and marketplace).</li>
                        <li>Process orders, payments, and deliveries, and communicate with you about them.</li>
                        <li>Personalize content, recommendations, and the ads you see.</li>
                        <li>Maintain the security and integrity of the Site and prevent fraud or abuse.</li>
                        <li>Send administrative, transactional, and — where you have opted in — promotional communications.</li>
                        <li>Analyze usage trends to improve the Site's performance and features.</li>
                        <li>Comply with legal obligations and enforce our Terms &amp; Conditions.</li>
                    </ul>
                </motion.section>

                {/* Cookies & Tracking Technologies */}
                <motion.section ref={sectionRefs.cookies} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Cookies &amp; Tracking Technologies</h3>
                    <p>
                        We use cookies, web beacons, and similar tracking technologies to operate the Site, remember your preferences, keep you signed in, understand how the Site is used, and — as described below — to serve advertising.
                    </p>
                    <p>We use the following categories of cookies:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                        <li><strong>Strictly necessary cookies:</strong> required for core functionality such as login sessions and security. These cannot be switched off.</li>
                        <li><strong>Analytics/performance cookies:</strong> help us understand how visitors use the Site so we can improve it.</li>
                        <li><strong>Advertising cookies:</strong> used by us and third-party vendors, including Google, to serve ads that are more relevant to you and to measure ad performance, as described in the Advertising &amp; Google AdSense section below.</li>
                    </ul>
                    <p>
                        When you first visit the Site, you will be asked to accept or reject non-essential cookies through our cookie banner. You can change your preference at any time via the "Cookie Settings" link in the footer of the Site, or by adjusting your browser settings to block or delete cookies.
                    </p>
                </motion.section>

                {/* Advertising & Google AdSense */}
                <motion.section ref={sectionRefs.advertising} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Advertising &amp; Google AdSense</h3>
                    <p>
                        We use Google AdSense and may use other third-party advertising companies to serve ads when you visit the Site. These companies, including Google, use cookies and similar technologies to serve ads based on your prior visits to this website and other websites on the Internet.
                    </p>
                    <p>
                        Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to this Site and/or other sites on the Internet. We do not control the content of these ads or the specific cookies third-party vendors may set.
                    </p>
                    <p>You have the following choices regarding personalized advertising:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                        <li>
                            Manage or opt out of personalized advertising directly through{' '}
                            <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-customGold hover:underline">Google Ads Settings</a>.
                        </li>
                        <li>
                            Opt out of many third-party vendors' use of cookies for personalized advertising by visiting{' '}
                            <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-customGold hover:underline">www.aboutads.info</a>.
                        </li>
                        <li>
                            Reject advertising cookies on this Site at any time using our cookie consent banner or the "Cookie Settings" link in the footer.
                        </li>
                        <li>
                            Learn more about{' '}
                            <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-customGold hover:underline">
                                how Google uses information from sites or apps that use its services
                            </a>.
                        </li>
                    </ul>
                    <p>
                        If you are located in the European Economic Area, the United Kingdom, or Switzerland, we will only load advertising cookies and serve personalized ads after you provide consent through our cookie banner. If you decline, you may still see ads, but they will not be personalized based on your browsing activity.
                    </p>
                </motion.section>

                {/* How We Share Information */}
                <motion.section ref={sectionRefs.dataSharing} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">How We Share Information</h3>
                    <p>We may share your information with:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                        <li><strong>Service providers:</strong> hosting, cloud storage, analytics, customer support, and payment processing partners who process data on our behalf.</li>
                        <li><strong>Advertising partners:</strong> Google AdSense and similar vendors, as described above, and only to the extent you have consented where consent is required.</li>
                        <li><strong>Marketplace sellers:</strong> where necessary to fulfil an order placed on the Blackdefynition Marketplace.</li>
                        <li><strong>Other users:</strong> information you choose to make public (such as posts, profile details, or reviews) is visible to other users as intended by the relevant feature.</li>
                        <li><strong>Legal and safety purposes:</strong> when required by law, to enforce our Terms &amp; Conditions, or to protect the rights, property, or safety of Blackdefynition, our users, or the public.</li>
                    </ul>
                    <p>We do not sell your personal information for money.</p>
                </motion.section>

                {/* Data Retention */}
                <motion.section ref={sectionRefs.dataRetention} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Data Retention</h3>
                    <p>
                        We retain personal information for as long as your account is active or as needed to provide the Site's features, comply with our legal obligations, resolve disputes, and enforce our agreements. When information is no longer needed, we delete or anonymize it.
                    </p>
                </motion.section>

                {/* Your Rights & Choices */}
                <motion.section ref={sectionRefs.yourRights} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Your Rights &amp; Choices</h3>
                    <p>Depending on your location, you may have the right to:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                        <li>Access, correct, or update the personal information we hold about you (much of this can be done from your account settings).</li>
                        <li>Request deletion of your personal information.</li>
                        <li>Object to or restrict certain processing, including for advertising purposes.</li>
                        <li>Withdraw cookie consent at any time via the "Cookie Settings" link in the footer.</li>
                        <li>Opt out of promotional emails, SMS, and push notifications using the unsubscribe link or your notification settings.</li>
                        <li>Where applicable (for example, under GDPR or the California Consumer Privacy Act), request a copy of your data or lodge a complaint with your local data protection authority.</li>
                    </ul>
                    <p>
                        To exercise any of these rights, contact us at{' '}
                        <a href="mailto:privacy@blackdefynition.com" className="text-customGold hover:underline">privacy@blackdefynition.com</a>.
                    </p>
                </motion.section>

                {/* Children's Privacy */}
                <motion.section ref={sectionRefs.childrenPrivacy} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Children's Privacy</h3>
                    <p>
                        The Site is intended for users who are at least 18 years old, or who access the Site under the supervision of a parent or legal guardian, as set out in our Terms &amp; Conditions. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without appropriate consent, we will take steps to delete it.
                    </p>
                </motion.section>

                {/* International Data Transfers */}
                <motion.section ref={sectionRefs.international} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">International Data Transfers</h3>
                    <p>
                        We operate across Africa, the United States, and the United Kingdom, and we work with service providers located in other countries. Your information may therefore be transferred to, stored, and processed in a country other than your own. Where required, we take steps to ensure such transfers are subject to appropriate safeguards.
                    </p>
                </motion.section>

                {/* Data Security */}
                <motion.section ref={sectionRefs.security} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Data Security</h3>
                    <p>
                        We use reasonable administrative, technical, and physical safeguards designed to protect your information. However, no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.
                    </p>
                </motion.section>

                {/* Third-Party Links */}
                <motion.section ref={sectionRefs.thirdPartyLinks} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Third-Party Links</h3>
                    <p>
                        The Site may contain links to third-party websites, including seller storefronts on the Blackdefynition Marketplace and advertised sites. This Privacy Policy does not apply to those third-party sites, and we encourage you to review their privacy policies before providing any information.
                    </p>
                </motion.section>

                {/* Changes To This Policy */}
                <motion.section ref={sectionRefs.changes} initial="hidden" animate="visible" variants={contentVariants} className="mb-12 space-y-4 text-white">
                    <h3 className="text-xl font-semibold text-customGold uppercase">Changes To This Policy</h3>
                    <p>
                        We may update this Privacy Policy from time to time. Changes will be effective when posted on this page, with the "Last updated" date revised accordingly. Your continued use of the Site after changes are posted constitutes your acceptance of the updated policy.
                    </p>
                </motion.section>

                {/* Contact Us */}
                <motion.div
                    ref={sectionRefs.contact}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="bg-customGray border-l-4 border-customGold p-4 mt-12"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                >
                    <p className="text-white">
                        If you have any questions about this Privacy Policy or wish to exercise your data rights, contact us at{' '}
                        <a href="mailto:privacy@blackdefynition.com" className="hover:underline text-customGold">privacy@blackdefynition.com</a>.
                        For questions about these terms in general, see our{' '}
                        <Link to="/terms" className="hover:underline text-customGold">Terms &amp; Conditions</Link>.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
